/**
 * "Nombre completo" → first and last name, as Zoho and the Hub expect. Ported from the tour registration
 * (margarita.pasosdegigante.com, src/lib/nombre.ts) so "José Ángel Hernández" or "María de la Luz" split the same way.
 */

/** Words that stick to the next one: "de la Garza", "del Valle", "van der Berg". */
const PARTICLES = new Set(["de", "del", "la", "las", "los", "y", "da", "das", "do", "dos", "di", "van", "von", "der"]);

/**
 * Common first names in Mexico and Latin America (no accents, lowercase): when the second word is one of these it
 * belongs to the first name ("José Ángel", "María de la Luz"). Without a match the split goes by word count.
 */
const FIRST_NAMES = new Set(
  `adriana alberto alejandra alejandro alicia ana andrea andres angel angela angelica antonio armando
  beatriz brenda carlos carmen carolina cecilia claudia cristina daniel daniela david diana diego eduardo
  elena elizabeth enrique ernesto esther eugenia fernanda fernando francisco gabriel gabriela gerardo
  guadalupe guillermo gustavo hector hugo ignacio isabel ivan javier jesus jorge jose josefina juan julia
  julio karla laura leticia lorena lucia luis luisa luz manuel margarita maria mariana mario marisol
  martha miguel monica natalia norma oscar pablo patricia paola pedro rafael ramon raul ricardo roberto
  rocio rosa rosario ruben salvador sandra sergio silvia sofia teresa valeria veronica victor ximena
  yolanda`.split(/\s+/)
);

const withoutAccents = (text: string) => text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
const isFirstName = (part: string) => FIRST_NAMES.has(withoutAccents(part.split(" ").at(-1)!));

/**
 * "Ana María López García" → { firstName: "Ana María", lastName: "López García" }. Particles count with the next
 * word; following parts that are common first names join the first name; otherwise 4+ parts put 2 in the first
 * name and 3 parts put 1. At least one part is always the last name, and joining both gives back what the person
 * typed. A single word returns `undefined` (the last name is missing).
 */
export function splitFullName(full: string): { firstName: string; lastName: string } | undefined {
  const words = full.trim().split(/\s+/).filter(Boolean);
  const parts: string[] = [];
  let pending: string[] = [];
  for (const word of words) {
    pending.push(word);
    if (!PARTICLES.has(word.toLowerCase())) {
      parts.push(pending.join(" "));
      pending = [];
    }
  }
  // Trailing particles ("Juan de") go with the last part.
  if (pending.length) {
    if (parts.length) parts[parts.length - 1] += ` ${pending.join(" ")}`;
    else parts.push(pending.join(" "));
  }
  if (parts.length < 2) return undefined;
  let inFirstName = 1;
  while (inFirstName < parts.length - 1 && isFirstName(parts[inFirstName]!)) inFirstName++;
  if (inFirstName === 1 && parts.length >= 4) inFirstName = 2;
  return { firstName: parts.slice(0, inFirstName).join(" "), lastName: parts.slice(inFirstName).join(" ") };
}

export const MISSING_LAST_NAME = "Escribe tu nombre y apellido.";
